import { useEffect } from 'react';
import { useWidgetTokenStore } from '@elfsight/lib-utils';
import { useSettings } from '@wu/widget-core';
import type { LanguageCode } from '../../types';
import type { CustomTranslationsMap } from '../utils/custom-translations';
import { getAllShadowRoots } from '../utils/get-all-shadow-roots';
import { translatePage } from './translate-page';

interface UseTranslateDynamicContentProps {
  sourceLanguage?: string;
  targetLanguage?: LanguageCode;
  customTranslations?: CustomTranslationsMap;
}

export function useTranslateDynamicContent({
  sourceLanguage,
  targetLanguage,
  customTranslations
}: UseTranslateDynamicContentProps) {
  const { translationExclusions } = useSettings();
  const widgetTokenStore = useWidgetTokenStore();

  useEffect(() => {
    if (!targetLanguage) {
      return;
    }

    const observedShadowRoots = new WeakSet<ShadowRoot>();
    const observers: MutationObserver[] = [];

    const observeShadowRoot = (shadowRoot: ShadowRoot) => {
      if (observedShadowRoots.has(shadowRoot)) {
        return;
      }
      observedShadowRoots.add(shadowRoot);

      const shadowObserver = new MutationObserver((mutations) => {
        mutations.forEach(({ addedNodes }) => {
          processAddedNodes(addedNodes);
        });
      });

      shadowObserver.observe(shadowRoot, {
        childList: true,
        subtree: true
      });

      observers.push(shadowObserver);
    };

    const processAddedNodes = (nodes: NodeList | Element[]) => {
      nodes.forEach((node) => {
        translatePage({
          sourceLanguage,
          targetLanguage,
          element: node,
          widgetToken: widgetTokenStore.get(),
          translationExclusions,
          customTranslations
        });

        if (node instanceof Element) {
          getAllShadowRoots(node).forEach(observeShadowRoot);
        }
      });
    };

    const mainObserver = new MutationObserver((mutations) => {
      mutations.forEach(({ addedNodes }) => {
        processAddedNodes(addedNodes);
      });
    });

    mainObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    observers.push(mainObserver);

    getAllShadowRoots(document.body).forEach(observeShadowRoot);

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [
    targetLanguage,
    sourceLanguage,
    translationExclusions,
    customTranslations
  ]);
}
